
import React, { useState } from 'react';
import { Plus, Search, Edit, Trash2, Users, Mail, Loader2, X, Filter } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from '@/components/ui/pagination';
import { useUsers } from '@/hooks/useUsers';
import { useClients } from '@/hooks/useClients';
import { useClientFilter } from '@/hooks/useClientFilter';
import { NewUserDialog } from './NewUserDialog';
import { EditUserDialog } from './EditUserDialog';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

const UserManagement = () => {
  const { users, loading, deleteUser } = useUsers();
  const { clients } = useClients();
  const { canManageUsers, canManageUser, isAdmin, isClientAdmin } = useClientFilter();
  const [searchTerm, setSearchTerm] = useState('');
  const [showNewUserDialog, setShowNewUserDialog] = useState(false);
  const [editingUser, setEditingUser] = useState<any>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [sendingInvite, setSendingInvite] = useState<string | null>(null);
  const [filterColumn, setFilterColumn] = useState<string>('');
  const [filterValue, setFilterValue] = useState<string>('');
  const { toast } = useToast();
  
  const USERS_PER_PAGE = 6;

  const filteredUsers = users.filter(user => {
    // Filtro de busca por texto
    const matchesSearch = user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (user.client?.name && user.client.name.toLowerCase().includes(searchTerm.toLowerCase()));
    
    if (!matchesSearch) return false;
    
    // Filtro por coluna específica
    if (!filterColumn || !filterValue) return true;
    
    switch (filterColumn) {
      case 'cliente':
        return user.clients?.some((c: any) => c.id === filterValue) || 
               user.client?.id === filterValue;
      case 'funcao':
        return user.role === filterValue;
      case 'status':
        return user.status === filterValue;
      default:
        return true;
    }
  }).sort((a, b) => a.name.localeCompare(b.name, 'pt-BR', { sensitivity: 'base' }));

  // Pagination calculations
  const totalPages = Math.ceil(filteredUsers.length / USERS_PER_PAGE);
  const startIndex = (currentPage - 1) * USERS_PER_PAGE;
  const endIndex = startIndex + USERS_PER_PAGE;
  const currentUsers = filteredUsers.slice(startIndex, endIndex);

  // Reset to first page when search or filters change
  React.useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, filterColumn, filterValue]);
  
  const clearFilters = () => {
    setFilterColumn('');
    setFilterValue('');
  };
  
  const uniqueRoles = Array.from(new Set(users.map(u => u.role)));

  // Smart pagination logic
  const getPaginationItems = () => {
    const items = [];
    const maxVisible = 5; // Maximum number of page buttons to show
    
    if (totalPages <= maxVisible) {
      // Show all pages if total is small
      for (let i = 1; i <= totalPages; i++) {
        items.push(i);
      }
    } else {
      // Always show first page
      items.push(1);
      
      let start = Math.max(2, currentPage - 1);
      let end = Math.min(totalPages - 1, currentPage + 1);
      
      // Add ellipsis after first page if needed
      if (start > 2) {
        items.push('ellipsis-start');
        start = Math.max(start, currentPage - 1);
      }
      
      // Add middle pages
      for (let i = start; i <= end; i++) {
        if (i !== 1 && i !== totalPages) {
          items.push(i);
        }
      }
      
      // Add ellipsis before last page if needed
      if (end < totalPages - 1) {
        items.push('ellipsis-end');
      }
      
      // Always show last page
      if (totalPages > 1) {
        items.push(totalPages);
      }
    }
    
    return items;
  };

  const handleDeleteUser = async (user: any) => {
    // Verificar permissão para excluir
    if (!canManageUser(user.client_id, user.role)) {
      toast({
        title: "Acesso negado",
        description: "Você não tem permissão para excluir este usuário.",
        variant: "destructive",
      });
      return;
    }
    
    if (window.confirm('Tem certeza que deseja excluir este usuário?')) {
      await deleteUser.mutateAsync(user.id);
    }
  };

  const handleSendInvite = async (user: any) => {
    setSendingInvite(user.id);
    
    try {
      const { data, error } = await supabase.functions.invoke('invite-user', {
        body: {
          email: user.email,
          name: user.name,
          role: user.role,
          redirectTo: `${window.location.origin}/auth/callback`,
        }
      });

      // If user already exists in Auth, send reset password email
      if (!error && (data as any)?.status === 'already_exists') {
        const { error: resetError } = await supabase.auth.resetPasswordForEmail(user.email, {
          redirectTo: `${window.location.origin}/set-password?type=recovery`,
        });
        if (resetError) {
          toast({
            variant: "destructive",
            title: "Não foi possível enviar o e-mail de acesso",
            description: resetError.message || "Tente novamente em instantes.",
          });
        } else {
          toast({
            title: "E-mail enviado",
            description: `Já existia uma conta para ${user.email}. Enviamos um link para definir/redefinir a senha.`,
          });
        }
      } else if (error) {
        // Qualquer erro ao convidar: tenta fluxo alternativo de redefinição de senha
        const { error: resetError } = await supabase.auth.resetPasswordForEmail(user.email, {
          redirectTo: `${window.location.origin}/set-password?type=recovery`,
        });

        if (resetError) {
          toast({
            variant: "destructive",
            title: "Erro ao enviar convite",
            description: (error as any)?.message || "Ocorreu um erro inesperado.",
          });
        } else {
          toast({
            title: "E-mail enviado",
            description: `Enviamos um link para ${user.email} definir ou redefinir a senha.`,
          });
        }
      } else {
        toast({
          title: "Convite enviado!",
          description: `Convite enviado para ${user.email}. O usuário receberá um email com instruções para definir a senha.`,
        });
      }
    } catch (err: any) {
      const msg = err?.message || '';
      // Também tratar o caso de erro de rede 400 vindo da edge function
      if (msg.includes('400') || msg.includes('email_exists') || msg.includes('already been registered')) {
        const { error: resetError } = await supabase.auth.resetPasswordForEmail(user.email, {
          redirectTo: `${window.location.origin}/set-password?type=recovery`,
        });
        if (!resetError) {
          toast({
            title: "E-mail de redefinição enviado",
            description: `Já existia uma conta para ${user.email}. Enviamos um link para redefinição de senha.`,
          });
          setSendingInvite(null);
          return;
        }
      }

      toast({
        variant: "destructive",
        title: "Erro ao enviar convite",
        description: err.message || "Ocorreu um erro inesperado.",
      });
    } finally {
      setSendingInvite(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg">Carregando usuários...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-2">
            <Users className="h-8 w-8" />
            Usuários
          </h1>
          <p className="text-gray-600 mt-2">
            Gerencie os usuários do sistema com suporte multi-cliente
          </p>
        </div>
        {canManageUsers() && (
          <Button 
            onClick={() => setShowNewUserDialog(true)}
            className="flex items-center gap-2"
          >
            <Plus className="h-4 w-4" />
            Novo Usuário
          </Button>
        )}
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Lista de Usuários</CardTitle>
          <div className="flex items-center gap-4 flex-wrap">
            <div className="relative flex-1 min-w-[250px] max-w-sm">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
              <Input
                placeholder="Buscar usuários..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            
            <div className="flex items-center gap-2 border-l pl-4">
              <Filter className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm font-medium">Filtrar por:</span>
            </div>
              <Select value={filterColumn} onValueChange={(value) => {
                setFilterColumn(value);
                setFilterValue('');
              }}>
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="Selecionar coluna" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="cliente">Cliente</SelectItem>
                  <SelectItem value="funcao">Função</SelectItem>
                  <SelectItem value="status">Status</SelectItem>
                </SelectContent>
              </Select>
              
              {filterColumn && (
                <>
                  {filterColumn === 'cliente' && (
                    <Select value={filterValue} onValueChange={setFilterValue}>
                      <SelectTrigger className="w-[200px]">
                        <SelectValue placeholder="Selecionar cliente" />
                      </SelectTrigger>
                      <SelectContent>
                        {clients.map((client) => (
                          <SelectItem key={client.id} value={client.id}>
                            {client.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                  
                  {filterColumn === 'funcao' && (
                    <Select value={filterValue} onValueChange={setFilterValue}>
                      <SelectTrigger className="w-[200px]">
                        <SelectValue placeholder="Selecionar função" />
                      </SelectTrigger>
                      <SelectContent>
                        {uniqueRoles.map((role) => (
                          <SelectItem key={role} value={role}>
                            {role}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                  
                  {filterColumn === 'status' && (
                    <Select value={filterValue} onValueChange={setFilterValue}>
                      <SelectTrigger className="w-[200px]">
                        <SelectValue placeholder="Selecionar status" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="active">Ativo</SelectItem>
                        <SelectItem value="inactive">Inativo</SelectItem>
                      </SelectContent>
                    </Select>
                  )}
                  
                  {filterValue && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={clearFilters}
                      className="gap-1"
                    >
                      <X className="h-4 w-4" />
                      Limpar filtros
                    </Button>
                  )}
                </>
              )}
              
              <div className="text-sm text-gray-500 ml-auto">
                {filteredUsers.length} usuário(s) encontrado(s)
              </div>
            </div>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Nome</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Cliente</TableHead>
                <TableHead>Função</TableHead>
                <TableHead>Departamento</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Ações</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {currentUsers.map((user) => (
                <TableRow key={user.id}>
                  <TableCell className="font-medium">{user.name}</TableCell>
                  <TableCell>{user.email}</TableCell>
                  <TableCell>
                    {user.clients && user.clients.length > 0 ? (
                      <div className="flex flex-wrap gap-1 max-w-xs">
                        {user.clients.map((client: any) => (
                          <Badge key={client.id} variant="outline" className="whitespace-nowrap">
                            {client.name}
                          </Badge>
                        ))}
                      </div>
                    ) : user.client ? (
                      <Badge variant="outline">{user.client.name}</Badge>
                    ) : (
                      <span className="text-gray-400">Sem cliente</span>
                    )}
                  </TableCell>
                  <TableCell>
                    <Badge variant="secondary">
                      {{ admin: 'Admin Full', super_admin: 'Super Admin', client_admin: 'Admin do Cliente', manager: 'Gerente', technician: 'Técnico', user: 'Usuário' }[user.role] || user.role}
                    </Badge>
                  </TableCell>
                  <TableCell>{user.department || '-'}</TableCell>
                  <TableCell>
                    <Badge 
                      variant={user.status === 'active' ? 'default' : 'secondary'}
                      className={user.status === 'active' ? 'bg-green-500' : ''}
                    >
                      {user.status === 'active' ? 'Ativo' : 'Inativo'}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      {user.status === 'inactive' && canManageUser(user.client_id, user.role) && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleSendInvite(user)}
                          disabled={sendingInvite === user.id}
                          className="text-blue-600 hover:text-blue-900"
                          title="Enviar convite por email"
                        >
                          {sendingInvite === user.id ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <Mail className="h-4 w-4" />
                          )}
                        </Button>
                      )}
                      {canManageUser(user.client_id, user.role) && (
                        <>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setEditingUser(user)}
                            title="Editar usuário"
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteUser(user)}
                            className="text-red-600 hover:text-red-900"
                            title="Excluir usuário"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
              {currentUsers.length === 0 && filteredUsers.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8">
                    <div className="text-gray-500">
                      {searchTerm ? 'Nenhum usuário encontrado com os critérios de busca.' : 'Nenhum usuário cadastrado.'}
                    </div>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
        
        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex justify-center p-4">
            <Pagination>
              <PaginationContent>
                <PaginationItem>
                  <PaginationPrevious 
                    onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                    className={currentPage === 1 ? 'pointer-events-none opacity-50' : 'cursor-pointer'}
                  />
                </PaginationItem>
                
                {getPaginationItems().map((item, index) => {
                  if (typeof item === 'string' && item.startsWith('ellipsis')) {
                    return (
                      <PaginationItem key={item}>
                        <PaginationEllipsis />
                      </PaginationItem>
                    );
                  }
                  
                  const page = item as number;
                  return (
                    <PaginationItem key={page}>
                      <PaginationLink
                        onClick={() => setCurrentPage(page)}
                        isActive={currentPage === page}
                        className="cursor-pointer"
                      >
                        {page}
                      </PaginationLink>
                    </PaginationItem>
                  );
                })}
                
                <PaginationItem>
                  <PaginationNext 
                    onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                    className={currentPage === totalPages ? 'pointer-events-none opacity-50' : 'cursor-pointer'}
                  />
                </PaginationItem>
              </PaginationContent>
            </Pagination>
          </div>
        )}
      </Card>

      <NewUserDialog
        open={showNewUserDialog}
        onOpenChange={setShowNewUserDialog}
      />

      {editingUser && (
        <EditUserDialog
          user={editingUser}
          open={!!editingUser}
          onOpenChange={(open) => !open && setEditingUser(null)}
        />
      )}
    </div>
  );
};

export default UserManagement;
